import { history } from "../../_Bootstrap/RoutingAndStore";
import type { LocalDatabaseCustomer, LocalDatabaseProduct } from "../../_Generated/Sync.gen";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import Request from "../../Utilities/Platform/Request";
import PopupManager from "../Popups/PopupManager";

type PostMessageCallback = (message: any, target: "*") => void;

let PageRuleAPIListeners: Array<(type: string, data: any) => void> = [];

export default class PageRuleAPIHandler {
    private static redirectCheck: undefined | ((to: string) => boolean);

    public static handle(
        data: any & {
            type: string;
            id: string;
        },
        postMessage: PostMessageCallback
    ): void {
        switch(data.type) {
            case "request":
                this.handleRequest(data, postMessage);

                return;
            case "redirect":
                this.handleRedirect(data);

                return;
            case "field":
                this.handleField(data);

                return;
            case "toast":
                this.handleToast(data);

                return;
            case "findByField":
                this.findByField(data, postMessage);

                return;
            case "back":
            case "next":
            case "finish":
                this.emit(data.type, data);

                return;
        }

        console.error("Unhandled API request:", data);
    }

    protected static returnData(postMessage: PostMessageCallback, data: unknown): void {
        try {
            postMessage({
                type: "__sandbox_api_reply",
                data,
            }, "*");
        } catch(e) {
            console.error("Cannot post the message to the sandbox", e);
        }
    }

    protected static handleRequest(
        data: {
            method: "GET" | "POST" | "PUT";
            url: string;
            body: unknown;
            id: string;
        },
        postMessage: PostMessageCallback
    ): Promise<void> {
        return Request({
            method: data.method,
            url   : data.url,
            data  : data.body,
        })
            .then(response => {
                return this.returnData(postMessage, {
                    id  : data.id,
                    data: response.data,
                });
            })
            .catch(err => {
                return this.returnData(postMessage, {
                    error  : true,
                    message: err.toString(),
                    id     : data.id,
                });
            });
    }

    protected static handleRedirect(
        data: {
            address: string;
        }
    ): void {
        if(typeof this.redirectCheck === "function") {
            if(!this.redirectCheck(data.address)) {
                return;
            }
        }

        if(data.address.indexOf("//") !== -1) {
            window.location.assign(data.address);

            return;
        }

        history.push(data.address);
    }

    protected static handleField(
        data: {
            field: string;
            value: any;
        }
    ): void {
        const { field, value } = data;

        this.emit("field", {
            field,
            value,
        });
    }

    protected static handleToast(
        data: {
            toastType: "info" | "warning" | "error" | "success";
            toastMessage: string;
        }
    ): void {
        const { toastType, toastMessage } = data;

        if(typeof PopupManager[toastType] === "function") {
            PopupManager[toastType](toastMessage);
        }
    }

    protected static findByField(
        data: {
            database: "products" | "customers";
            field: string;
            value: any;
            id: string;
        },
        postMessage: PostMessageCallback
    ): void {
        const { database, field, value, id } = data;

        if(database !== "products" && database !== "customers") {
            this.returnData(postMessage, {
                error  : true,
                message: "Invalid database specified",
                id,
            });

            return;
        }

        determineDataSource()
            .then(source => (
                source[database].all() as Promise<Array<LocalDatabaseCustomer> | Array<LocalDatabaseProduct>>
            ))
            .then((data: Array<LocalDatabaseCustomer | LocalDatabaseProduct>) => data.filter((
                item: LocalDatabaseProduct | LocalDatabaseCustomer
            ) => {
                if(typeof item[field as keyof typeof item] === "undefined") {
                    throw new Error("Invalid field specified");
                }

                return item[field as keyof typeof item] === value;
            }))
            .then(filtered => this.returnData(postMessage, {
                id,
                data: filtered,
            }))
            .catch(err => this.returnData(postMessage, {
                id,
                error  : true,
                message: err.toString(),
            }));
    }

    public static addListener(listener: (type: string, data: any) => void): void {
        PageRuleAPIListeners.push(listener);
    }

    public static removeListener(listener: (type: string, data: any) => void): void {
        const index = PageRuleAPIListeners.indexOf(listener);

        if(index === -1) {
            return;
        }

        PageRuleAPIListeners = [
            ...PageRuleAPIListeners.slice(0, index),
            ...PageRuleAPIListeners.slice(index + 1),
        ];
    }

    protected static emit(type: string, data: any): void {
        for(let i = 0, l = PageRuleAPIListeners.length; i < l; i++) {
            PageRuleAPIListeners[i](type, data);
        }
    }

    public static addRedirectCheck(redirectCheck: (to: string) => boolean): void {
        this.redirectCheck = redirectCheck;
    }

    public static removeRedirectCheck(): void {
        this.redirectCheck = undefined;
    }
}
